import {
  faClockRotateLeft,
  faFilterSlash,
  faInboxFull
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { AnimatePresence, motion } from 'motion/react'
import { useState } from 'react'
import { TypeFilter } from '~/components/Filters/TypeFilter'
import { NoResult } from '~/components/Global/Elements/NoResult/NoResult'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import {
  applicantModalAsideSection,
  applicantModalAsideSectionTitle,
  applicantModalAsideSectionTitleWithButton
} from '~/components/Scopes/Applicants/ApplicantModal/ApplicantModal.css'
import { useHistory } from '~/domains/actions/queries/useHistory'
import {
  type ApplicantHistoryEvent,
  filterableActionTypes
} from '~/domains/actions/types'
import { getEventChronoDate } from '~/domains/actions/utils/eventChronoDate'
import { themeClass } from '~/styles/theme.css'
import { formatRelativeTime } from '~/utils'
import { buildV3Url } from '~/utils/legacy'
import {
  historyItem,
  historyItemContent,
  historyItemHeader,
  historyItemTime,
  historyItemTitle,
  historyLoadMore,
  historySection,
  historySectionDate,
  historySectionItems,
  historyTitleActions,
  historyWrapper
} from './ApplicantHistory.css'
import { HistoryEventCard } from './HistoryCards'

const groupEventsByDate = (events: ApplicantHistoryEvent[]) => {
  const groups: {
    [key: string]: {
      relativeTime: string
      date: Date
      events: ApplicantHistoryEvent[]
    }
  } = {}

  for (const event of events) {
    const eventDate = getEventChronoDate(event)
    const date = new Date(eventDate)
    const relativeTime = formatRelativeTime(date, 3)
    if (!groups[relativeTime]) {
      groups[relativeTime] = {
        relativeTime,
        date,
        events: []
      }
    }
    groups[relativeTime].events.push(event)
  }

  return Object.values(groups)
}

const HistoryItemShimmer = () => (
  <div className={historyItem}>
    <div className={historyItemHeader}>
      <Shimmer height={16} width={16} />
      <div className={historyItemTitle}>
        <Shimmer height={16} width={120} />
      </div>
      <div className={historyItemTime}>
        <Shimmer height={14} width={50} />
      </div>
    </div>
    <div className={historyItemContent}>
      <Shimmer height={14} width={200} />
    </div>
  </div>
)

const HistorySectionShimmer = () => (
  <div className={historySection}>
    <div className={historySectionDate}>
      <Shimmer height={16} width={100} />
    </div>
    <div className={historySectionItems}>
      <HistoryItemShimmer />
      <HistoryItemShimmer />
      <HistoryItemShimmer />
    </div>
  </div>
)

export const ApplicantHistory = ({ applicantId }: { applicantId: number }) => {
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set())

  const {
    items: historyItems,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    totalItems,
    isError
  } = useHistory({
    entityType: 'applicant',
    entityId: applicantId,
    selectedTypes
  })

  const filteredEvents =
    historyItems?.filter(
      (event) => selectedTypes.size === 0 || selectedTypes.has(event.type || '')
    ) || []

  const groupedEvents = groupEventsByDate(filteredEvents)

  return (
    <div className={clsx(applicantModalAsideSection, themeClass)}>
      <div
        className={clsx(
          applicantModalAsideSectionTitle,
          applicantModalAsideSectionTitleWithButton
        )}
      >
        <div>
          <FontAwesomeIcon icon={faClockRotateLeft} /> Historique de relation
        </div>

        <div className={historyTitleActions}>
          <Tooltip text="Messagerie" asChild>
            <Button
              variation={ButtonVariation.Neutral}
              buttonSize="small"
              href={buildV3Url(`/messaging?type=applicant&id=${applicantId}`)}
            >
              <FontAwesomeIcon icon={faInboxFull} />
            </Button>
          </Tooltip>
          <TypeFilter
            options={filterableActionTypes}
            selectedTypes={selectedTypes}
            onTypesChange={(types) => setSelectedTypes(new Set(types))}
          />
        </div>
      </div>

      <AnimatePresence>
        <motion.div
          className={clsx(themeClass, historyWrapper)}
          variants={{
            hidden: { opacity: 0 },
            visible: { opacity: 1, transition: { staggerChildren: 0.05 } }
          }}
          initial="hidden"
          animate="visible"
        >
          {groupedEvents.length === 0 && !isLoading && !isError && (
            <NoResult
              icon={faFilterSlash}
              message={
                selectedTypes.size === 0
                  ? 'Aucun historique avec ce candidat.'
                  : 'Aucun historique avec ces filtres.'
              }
              isSmall
            />
          )}

          {isError && (
            <Alert
              variation={AlertVariation.Danger}
              message="Une erreur est survenue lors de la récupération de l'historique."
              onDismiss={false}
            />
          )}

          {groupedEvents.map((group) => (
            <motion.div
              key={group.relativeTime}
              className={historySection}
              variants={{
                initial: { opacity: 0, y: -20 },
                hidden: { opacity: 0, y: -20 },
                visible: { opacity: 1, y: 0 }
              }}
            >
              <div
                className={historySectionDate}
                title={group.date.toLocaleDateString()}
              >
                {group.relativeTime}
              </div>
              <div className={historySectionItems}>
                {group.events.map((event) => (
                  <HistoryEventCard key={event.id} event={event} />
                ))}
              </div>
            </motion.div>
          ))}

          {isLoading && (
            <>
              <HistorySectionShimmer />
              <HistorySectionShimmer />
            </>
          )}

          {hasNextPage && (
            <Button
              className={historyLoadMore}
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              variation={ButtonVariation.Neutral}
              buttonSize="small"
            >
              {isFetchingNextPage ? (
                <Shimmer height={16} width={80} />
              ) : (
                `Afficher plus (+${totalItems - (historyItems?.length ?? 0)})`
              )}
            </Button>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}
